import React from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBag, AlertTriangle, TrendingDown, Lightbulb } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatAUD } from "@/lib/media";
import SaleCountdown from "@/components/SaleCountdown";
import { toast } from "sonner";

const wkey = (e) => `${e.id}::${e.size || ""}::${e.colour || ""}`;
const SELECT_CLS = "flex-1 min-w-0 rounded-full border border-[#19324A]/15 bg-[#FFF9EE] px-3 py-2 text-xs font-display font-semibold text-[#19324A] focus:outline-none focus:ring-2 focus:ring-[#FFD93D]";

// Derived state shared by the card's sub-components.
const describe = (entry) => {
  const p = entry.product;
  const sizes = p.sizes || [], colours = p.colours || [];
  const soldOut = (p.stock ?? 0) <= 0;
  const onSale = !!p.sale_price;
  const currentEff = onSale ? p.sale_price : p.price;
  const needsSize = sizes.length > 0 && !entry.size;
  const needsColour = colours.length > 0 && !entry.colour;
  return {
    p, sizes, colours, soldOut, onSale, needsSize, needsColour,
    lowStock: !soldOut && (p.stock ?? 0) <= 5,
    priceDropped: entry.saved_price != null && currentEff < entry.saved_price,
    blocked: soldOut || needsSize || needsColour,
    noteReminder: (needsSize || needsColour) && !(entry.note || "").trim(),
  };
};

function WishlistAlerts({ entry, d }) {
  return (
    <>
      {d.priceDropped && (
        <p className="inline-flex items-center gap-1 text-xs font-display font-bold text-[#7ED957]" data-testid={`wishlist-pricedrop-${d.p.slug}`}>
          <TrendingDown className="w-3.5 h-3.5" /> Price dropped — was {formatAUD(entry.saved_price)}!
        </p>
      )}
      {d.lowStock && (
        <p className="inline-flex items-center gap-1 text-xs font-display font-bold text-[#FF6B9D]" data-testid={`wishlist-lowstock-${d.p.slug}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Almost gone — only {d.p.stock} left!
        </p>
      )}
    </>
  );
}

function WishlistVariantPickers({ entry, d, onChange }) {
  if (d.sizes.length === 0 && d.colours.length === 0) return null;
  return (
    <div className="flex gap-2 mt-1">
      {d.colours.length > 0 && (
        <select value={entry.colour || ""} data-testid={`wishlist-colour-${d.p.slug}`} className={SELECT_CLS}
          onChange={(e) => onChange(entry, entry.size ?? null, e.target.value || null)}>
          <option value="">Colour…</option>
          {d.colours.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
      {d.sizes.length > 0 && (
        <select value={entry.size || ""} data-testid={`wishlist-size-${d.p.slug}`} className={SELECT_CLS}
          onChange={(e) => onChange(entry, e.target.value || null, entry.colour ?? null)}>
          <option value="">Size…</option>
          {d.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
    </div>
  );
}

function WishlistNote({ entry, d, onSave }) {
  const missing = [d.needsSize && "size", d.needsColour && "colour"].filter(Boolean).join(" & ");
  return (
    <>
      <input
        data-testid={`wishlist-note-${d.p.slug}`}
        defaultValue={entry.note || ""}
        onBlur={(e) => { if ((e.target.value || "") !== (entry.note || "")) onSave(entry, e.target.value); }}
        maxLength={140}
        placeholder="Gift note (e.g. size L please!)"
        className={`w-full rounded-full border bg-[#FFF9EE] px-3 py-2 text-xs text-[#19324A] placeholder:text-[#19324A]/40 focus:outline-none focus:ring-2 focus:ring-[#FFD93D] ${d.noteReminder ? "border-[#FFD93D]" : "border-[#19324A]/15"}`}
      />
      {d.noteReminder && (
        <p className="inline-flex items-start gap-1 text-[11px] leading-snug text-[#19324A]/60" data-testid={`wishlist-note-reminder-${d.p.slug}`}>
          <Lightbulb className="w-3.5 h-3.5 text-[#FFD93D] shrink-0 mt-px" /> Pick your {missing} or add a note so gift-givers get it right.
        </p>
      )}
    </>
  );
}

function WishlistCard({ entry }) {
  const { removeWishlistEntry, updateWishlistVariant, updateWishlistNote, moveToCart } = useStore();
  const d = describe(entry);
  const { p } = d;

  const handleMove = () => {
    if (d.blocked) return;
    moveToCart(entry);
    toast.success(`${p.name} added to your bag 🐾`);
  };

  return (
    <div className="relative bg-white rounded-3xl overflow-hidden soft-shadow card-hover flex flex-col" data-testid={`wishlist-card-${p.slug}`}>
      <button onClick={() => removeWishlistEntry(entry)} aria-label="Remove from wishlist" data-testid={`wishlist-remove-${p.slug}`}
        className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 hover:text-[#FF6B9D] text-[#19324A]/60 transition-transform">
        <X className="w-5 h-5" />
      </button>
      {d.soldOut && <span className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-display font-bold bg-[#19324A] text-white shadow-sm">SOLD OUT</span>}

      <Link to={`/product/${p.slug}`} className="block relative aspect-square bg-[#FFF9EE] overflow-hidden">
        <img src={(p.images || [])[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
      </Link>

      <div className="p-4 sm:p-5 flex flex-col flex-1 gap-2">
        <Link to={`/product/${p.slug}`} className="font-display font-semibold text-[#19324A] leading-snug hover:text-[#FF6B9D] transition-colors line-clamp-2">{p.name}</Link>
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-lg text-[#19324A]">{formatAUD(d.onSale ? p.sale_price : p.price)}</span>
          {d.onSale && <span className="text-sm text-[#19324A]/45 line-through">{formatAUD(p.price)}</span>}
        </div>
        {d.onSale && p.sale_ends_at && <SaleCountdown ends={p.sale_ends_at} compact testid={`wishlist-sale-countdown-${p.slug}`} />}
        <WishlistAlerts entry={entry} d={d} />
        <WishlistVariantPickers entry={entry} d={d} onChange={updateWishlistVariant} />
        <WishlistNote entry={entry} d={d} onSave={updateWishlistNote} />
        <button onClick={handleMove} disabled={d.blocked} data-testid={`wishlist-move-${p.slug}`}
          className="btn-primary mt-auto w-full !py-2.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0">
          <ShoppingBag className="w-4 h-4" />
          {d.soldOut ? "Sold Out" : (d.needsSize || d.needsColour) ? "Choose options" : "Move to Bag"}
        </button>
      </div>
    </div>
  );
}

export default function WishlistGrid({ entries, testid = "wishlist-grid" }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid={testid}>
      {entries.map((e) => <WishlistCard key={wkey(e)} entry={e} />)}
    </div>
  );
}
